import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Plus, Trash2, Save, Award } from "lucide-react";
import { Badge } from "../Achievements";

export default function AdminAchievements() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", icon: "trophy", tier: "gold", category: "tournament", points: 100, season: "2026" });
  const [award, setAward] = useState({ user_id: "", achievement_id: "", context: "" });
  const [error, setError] = useState("");

  const load = async () => {
    const [a, u] = await Promise.all([api.get("/achievements"), api.get("/users")]);
    setItems(a.data);
    setUsers(u.data);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/achievements", form);
      setForm({ title: "", description: "", icon: "trophy", tier: "gold", category: "tournament", points: 100, season: "2026" });
      setCreating(false);
      load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Erfolg löschen?")) return;
    await api.delete(`/achievements/${id}`);
    load();
  };

  const grant = async (e) => {
    e.preventDefault();
    try {
      await api.post("/achievements/award", award);
      setAward({ user_id: "", achievement_id: "", context: "" });
      alert("Erfolg vergeben!");
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="admin-achievements-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Erfolge</div>
          <h1 className="font-display text-3xl tracking-tight mt-2">Pokale & Medaillen</h1>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary" data-testid="create-ach-btn"><Plus className="w-4 h-4" /> Neuer Erfolg</button>
      </div>

      {error && <div className="text-red-400 text-sm font-mono">{error}</div>}

      {creating && (
        <form onSubmit={create} className="card-solid p-5 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ach-form">
          {[["title","Titel"],["description","Beschreibung"],["season","Saison"]].map(([k,l]) => (
            <div key={k} className={k==="description"?"md:col-span-3":""}>
              <label className="label">{l}</label>
              <input className="input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required={k==="title"} data-testid={`ach-${k}`} />
            </div>
          ))}
          <div>
            <label className="label">Icon</label>
            <select className="input" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} data-testid="ach-icon">
              {["trophy","medal","crown","star","shield"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Tier</label>
            <select className="input" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="ach-tier">
              {["bronze","silver","gold","platinum","diamond"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Kategorie</label>
            <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ach-category">
              {["tournament","season","special","milestone"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Punkte</label>
            <input type="number" className="input" value={form.points} onChange={(e) => setForm({ ...form, points: parseInt(e.target.value || "0", 10) })} data-testid="ach-points" />
          </div>
          <div className="md:col-span-3 flex gap-2">
            <button type="submit" className="btn-primary"><Save className="w-4 h-4" /> Erstellen</button>
            <button type="button" onClick={() => setCreating(false)} className="btn-secondary">Abbrechen</button>
          </div>
        </form>
      )}

      <div className="card-solid p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient mb-3 flex items-center gap-2">
          <Award className="w-3.5 h-3.5" /> Vergeben
        </div>
        <form onSubmit={grant} className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="grant-form">
          <select required className="input" value={award.user_id} onChange={(e) => setAward({ ...award, user_id: e.target.value })} data-testid="grant-user">
            <option value="">— Mitglied —</option>
            {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
          </select>
          <select required className="input" value={award.achievement_id} onChange={(e) => setAward({ ...award, achievement_id: e.target.value })} data-testid="grant-ach">
            <option value="">— Erfolg —</option>
            {items.map((a) => <option key={a.id} value={a.id}>{a.title} ({a.tier})</option>)}
          </select>
          <input className="input" placeholder="Kontext (z.B. Winter Cup 2026)" value={award.context} onChange={(e) => setAward({ ...award, context: e.target.value })} data-testid="grant-context" />
          <div className="md:col-span-3">
            <button type="submit" className="btn-primary" data-testid="grant-submit"><Award className="w-4 h-4" /> Vergeben</button>
          </div>
        </form>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {items.map((a) => (
          <div key={a.id} className="card-solid p-4 space-y-3 flex flex-col items-center" data-testid={`ach-${a.id}`}>
            <Badge ach={a} size="md" />
            <div className="text-center text-xs text-white/60">{a.description}</div>
            <button onClick={() => remove(a.id)} className="btn-ghost !p-2 text-red-400" data-testid={`delete-ach-${a.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
